import { LOGIN_RETURN_URL_KEY } from "constants/login";
import { cookiePreference } from "core/cookie";
import React, { createContext, ReactNode, useContext, useEffect } from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";

import { useMatomo } from "@jonkoops/matomo-tracker-react";

import {
  CognitoAuthContext,
  ICognitoAuthContextState
} from "./CognitoAuthContext";

// Context interface
interface CognitoAuthTrackingContextType {}

// Create the react context (with an initial dummy instance)
export const CognitoAuthTrackingContext = createContext<
  CognitoAuthTrackingContextType
>({});
CognitoAuthTrackingContext.displayName = "CognitoAuthTrackingContext";

// Save the route for the requested page so we can return the user to this page after login.
const saveReturnUrl = (pathname: string): void => {
  sessionStorage.setItem(LOGIN_RETURN_URL_KEY, pathname);
};

const getReturnUrl = (): string | null =>
  sessionStorage
    .getItem(LOGIN_RETURN_URL_KEY)
    ?.toLowerCase()
    ?.trim() || null;

// Export the provider as we need to wrap the entire app with it
export function CognitoAuthTrackingProvider({
  children
}: {
  children: ReactNode;
}): JSX.Element {
  const { state, user, setError } = useContext(CognitoAuthContext);
  const { pushInstruction } = useMatomo();

  const history = useHistory();
  const location = useLocation();

  // Reset the error state if we change page
  useEffect(() => {
    setError("");
  }, [location.pathname, setError]);

  // If the user changes update the Matomo tracking
  useEffect(() => {
    try {
      if (cookiePreference("analytics") && user?.email) {
        pushInstruction("setUserId", user.email);
      }
    } catch (err) {
      if (err instanceof Error) {
        throw new Error(`Error user analytics user id not set: ${err.message}`);
      } else {
        throw new Error(`Error user analytics user id not set: ${err}`);
      }
    }

    const consentGiven = cookiePreference("analytics")
      ? "setConsentGiven"
      : "forgetConsentGiven";

    // Mark that the current user has consented. The consent is one-time only,
    // so in a subsequent browser session, the user will have to consent again.
    pushInstruction(consentGiven);
  }, [pushInstruction, user?.email]);

  // Track the login state changes and update the history on error or logout
  useEffect(() => {
    switch (state) {
      case ICognitoAuthContextState.TokenRefreshFailed:
        // DaveTODO - If the token refresh failed should we redirect to the login page?  Can we keep the current URL?
        saveReturnUrl(window.location.pathname);
        history.push("/login");
        break;
      case ICognitoAuthContextState.Errored:
        history.push("/login");
        break;
      case ICognitoAuthContextState.LoggedOut:
        saveReturnUrl(window.location.pathname);
        history.push("/login");
        break;
      case ICognitoAuthContextState.LoggedIn:
        const returnUrl = getReturnUrl();
        // Clear return url from local session as no longer needed.
        saveReturnUrl("");
        if (
          returnUrl?.startsWith("/") &&
          !["/login", "/logout"].includes(returnUrl)
        ) {
          // Redirect to the return url.
          history.push(returnUrl);
        }
        break;
    }
  }, [history, state]);

  return (
    <CognitoAuthTrackingContext.Provider value={{}}>
      {children}
    </CognitoAuthTrackingContext.Provider>
  );
}
